import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, ShieldCheck, Spinner, ArrowsClockwise, Plus, Trash, X,
  Warning, FloppyDisk, Phone, UsersThree, Headset, ArrowRight,
  CheckCircle, Buildings, Megaphone, Wrench, ArrowSquareOut, Bug,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import {
  fetchCallRoutingRules, updateCallRoutingRules,
  fetchStaffPositions, updateStaffPosition,
  previewVoiceWebhooks, syncVoiceWebhooks,
  fetchWarrantyBriefings, fetchErrorLog, fetchErrorRow,
} from "@/lib/api";
import AccountMenu from "@/components/AccountMenu";
import { useT } from "@/lib/i18n";

/**
 * /admin/call-routing — staff-only page that bundles:
 *   • Voice routing rules (rule list + default forwarding numbers + voicemail)
 *   • Staff↔Position grid (UsAirPeople with editable Position/Department/Phone)
 *   • Twilio voice-webhook sync (one-click drift fix)
 * Backend mounts these under /api/admin/call-routing/* + /api/admin/staff-positions
 * + /api/admin/twilio-credentials/voice-config/*.
 */
export default function CallRoutingPage({ user, onLogout, onLogoutAll }) {
  const navigate = useNavigate();
  const t = useT();
  const [tab, setTab] = useState("rules"); // "rules" | "staff" | "webhook" | "briefings" | "errors"

  return (
    <div className="min-h-screen bg-slate-50" data-testid="call-routing-page">
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-14 flex items-center gap-3">
          <button onClick={() => navigate("/")} className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500" data-testid="call-routing-back">
            <ArrowLeft size={18} weight="bold" />
          </button>
          <Headset size={20} weight="fill" className="text-blue-700" />
          <h1 className="text-base md:text-lg font-black tracking-tighter font-['Chivo']">{t("nav.call_routing", "Call Routing")}</h1>
          <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 rounded-sm">{t("help.role_admin", "Admin")}</span>
          <AccountMenu user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} />
        </div>
        <div className="max-w-6xl mx-auto px-4 md:px-8 flex items-center gap-1 border-t border-slate-100">
          <TabBtn id="rules" current={tab} setCurrent={setTab} icon={Megaphone} label={t("call_routing.tab_voice_rules", "Voice Rules")} />
          <TabBtn id="staff" current={tab} setCurrent={setTab} icon={UsersThree} label={t("call_routing.tab_staff_position", "Staff ↔ Position")} />
          <TabBtn id="webhook" current={tab} setCurrent={setTab} icon={ArrowsClockwise} label={t("call_routing.tab_webhook_sync", "Webhook Sync")} />
          <TabBtn id="briefings" current={tab} setCurrent={setTab} icon={Wrench} label={t("call_routing.tab_briefings", "Briefings")} />
          <TabBtn id="errors" current={tab} setCurrent={setTab} icon={Bug} label={t("call_routing.tab_errors", "Errors")} />
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 md:p-8">
        {tab === "rules" && <RulesTab />}
        {tab === "staff" && <StaffTab />}
        {tab === "webhook" && <WebhookTab />}
        {tab === "briefings" && <BriefingsTab />}
        {tab === "errors" && <ErrorsTab user={user} />}
      </main>
    </div>
  );
}

function TabBtn({ id, current, setCurrent, icon: Icon, label }) {
  const active = current === id;
  return (
    <button
      onClick={() => setCurrent(id)}
      className={`inline-flex items-center gap-1.5 text-xs px-3 py-2 border-b-2 ${
        active ? "border-blue-700 text-blue-700 font-bold" : "border-transparent text-slate-500 hover:text-slate-800"
      }`}
      data-testid={`call-routing-tab-${id}`}
    >
      <Icon size={14} weight={active ? "fill" : "regular"} /> {label}
    </button>
  );
}

// ─── Voice rules tab ─────────────────────────────────────────────────
function RulesTab() {
  const t = useT();
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [savedAt, setSavedAt] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const r = await fetchCallRoutingRules();
      setRules(r);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Could not load rules");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onSave = async () => {
    if (!rules) return;
    setSaving(true); setError("");
    try {
      const r = await updateCallRoutingRules({
        rules: rules.rules || [],
        default_forward: rules.default_forward || [],
        default_voicemail: rules.default_voicemail || "",
        ring_timeout_sec: rules.ring_timeout_sec || 20,
      });
      setRules(r);
      setSavedAt(new Date().toLocaleTimeString());
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!rules) return <div className="p-8 text-center"><Spinner size={20} className="animate-spin text-slate-400 mx-auto" /></div>;

  const update = (patch) => setRules({ ...rules, ...patch });
  const updateRule = (i, patch) => update({ rules: rules.rules.map((r, idx) => idx === i ? { ...r, ...patch } : r) });
  const removeRule = (i) => update({ rules: rules.rules.filter((_, idx) => idx !== i) });
  const addRule = () => update({ rules: [...(rules.rules || []), {
    twilio_number: "", department: "", position: "", forward_to: [], voicemail_msg: "", timeout_sec: 20,
  }] });

  return (
    <div className="space-y-5">
      {error && <ErrorBanner error={error} onClose={() => setError("")} />}

      <Card title={t("call_routing.default_fallback_title", "Default fallback")} sub={t("call_routing.default_fallback_sub", "Used when no rule matches the dialed number / caller. Numbers ring sequentially.")}>
        <FieldRow label={t("call_routing.default_forward_label", "Default forward to (E.164)")}>
          <PhonesEditor
            phones={rules.default_forward || []}
            onChange={(arr) => update({ default_forward: arr })}
            testIdPrefix="default-forward"
          />
        </FieldRow>
        <FieldRow label={t("call_routing.default_voicemail_label", "Default voicemail message")}>
          <textarea
            value={rules.default_voicemail || ""}
            onChange={(e) => update({ default_voicemail: e.target.value })}
            className="w-full text-sm p-2 border border-slate-300 rounded-sm min-h-[64px]"
            placeholder={t("call_routing.voicemail_placeholder", "Spoken when no-one answers.")}
            data-testid="default-voicemail-input"
          />
        </FieldRow>
        <FieldRow label={t("call_routing.ring_timeout_label", "Ring timeout (sec)")}>
          <Input type="number" min={5} max={120} value={rules.ring_timeout_sec ?? 20}
            onChange={(e) => update({ ring_timeout_sec: parseInt(e.target.value || "20", 10) })}
            className="text-sm w-24" data-testid="ring-timeout-input" />
        </FieldRow>
      </Card>

      <Card title={t("call_routing.routing_rules_title", "Routing rules")} sub={t("call_routing.routing_rules_sub", "Earliest matching rule wins. Match by Twilio number first, then by Position/Department.")}>
        {(rules.rules || []).length === 0 ? (
          <div className="text-xs text-slate-500 italic py-4 text-center" data-testid="no-rules-empty">{t("call_routing.no_rules", "No rules — calls will use the default fallback.")}</div>
        ) : (
          <div className="space-y-3">
            {(rules.rules || []).map((r, i) => (
              <div key={i} className="border border-slate-200 rounded-sm p-3 bg-slate-50" data-testid={`rule-row-${i}`}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <FieldRow label={t("call_routing.match_twilio_number", "Match Twilio number")} small>
                    <Input value={r.twilio_number || ""} onChange={(e) => updateRule(i, { twilio_number: e.target.value })} className="text-sm font-mono" placeholder="+18005551212" data-testid={`rule-${i}-twilio`} />
                  </FieldRow>
                  <FieldRow label={t("call_routing.match_position", "Match position")} small>
                    <Input value={r.position || ""} onChange={(e) => updateRule(i, { position: e.target.value })} className="text-sm" placeholder={t("call_routing.sales_manager_placeholder", "Sales Manager")} data-testid={`rule-${i}-position`} />
                  </FieldRow>
                  <FieldRow label={t("call_routing.match_department", "Match department")} small>
                    <Input value={r.department || ""} onChange={(e) => updateRule(i, { department: e.target.value })} className="text-sm" placeholder="Sales" data-testid={`rule-${i}-dept`} />
                  </FieldRow>
                </div>
                <FieldRow label={t("call_routing.forward_to_override", "Forward to (overrides position/department)")}>
                  <PhonesEditor
                    phones={r.forward_to || []}
                    onChange={(arr) => updateRule(i, { forward_to: arr })}
                    testIdPrefix={`rule-${i}-fwd`}
                  />
                </FieldRow>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <FieldRow label={t("call_routing.custom_voicemail", "Custom voicemail (optional)")} small>
                    <Input value={r.voicemail_msg || ""} onChange={(e) => updateRule(i, { voicemail_msg: e.target.value })} className="text-sm" placeholder={t("call_routing.sales_closed_placeholder", "Sales is closed for the day.")} data-testid={`rule-${i}-voicemail`} />
                  </FieldRow>
                  <FieldRow label={t("call_routing.timeout_sec", "Timeout (sec)")} small>
                    <Input type="number" min={5} max={120} value={r.timeout_sec ?? 20} onChange={(e) => updateRule(i, { timeout_sec: parseInt(e.target.value || "20", 10) })} className="text-sm w-24" data-testid={`rule-${i}-timeout`} />
                  </FieldRow>
                </div>
                <div className="text-right">
                  <button onClick={() => removeRule(i)} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-rose-700 hover:text-rose-900" data-testid={`rule-${i}-delete`}>
                    <Trash size={11} weight="fill" /> Remove rule
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        <button onClick={addRule} className="mt-3 inline-flex items-center gap-1 text-xs px-3 py-1.5 border border-dashed border-slate-400 rounded-sm hover:bg-slate-100 text-slate-700" data-testid="add-rule-btn">
          <Plus size={12} weight="bold" /> Add rule
        </button>
      </Card>

      <div className="flex items-center justify-end gap-3">
        {savedAt && (
          <span className="text-[11px] text-emerald-700 inline-flex items-center gap-1">
            <CheckCircle size={12} weight="fill" /> Saved at {savedAt}
          </span>
        )}
        <button onClick={onSave} disabled={saving} className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-4 py-2 bg-blue-700 text-white rounded-sm hover:bg-blue-800 disabled:bg-slate-300" data-testid="save-rules-btn">
          {saving ? <><Spinner size={12} className="animate-spin" /> Saving…</> : <><FloppyDisk size={12} weight="fill" /> Save rules</>}
        </button>
      </div>
    </div>
  );
}

function PhonesEditor({ phones, onChange, testIdPrefix }) {
  const t = useT();
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...(phones || []), v]);
    setDraft("");
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {(phones || []).map((p, i) => (
          <span key={i} className="inline-flex items-center gap-1 text-xs bg-blue-50 border border-blue-200 text-blue-800 px-2 py-1 rounded-sm font-mono" data-testid={`${testIdPrefix}-chip-${i}`}>
            {p}
            <button onClick={() => onChange(phones.filter((_, idx) => idx !== i))} className="text-blue-500 hover:text-blue-800" aria-label="Remove"><X size={10} weight="bold" /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
          placeholder="+1 555 123 4567" className="text-sm font-mono flex-1" data-testid={`${testIdPrefix}-input`} />
        <button onClick={add} disabled={!draft.trim()} className="text-xs px-3 border border-slate-300 rounded-sm hover:bg-slate-100 disabled:opacity-40" data-testid={`${testIdPrefix}-add`}>
          {t("call_routing.add_phone", "Add")}
        </button>
      </div>
    </div>
  );
}

// ─── Staff ↔ Position tab ────────────────────────────────────────────
function StaffTab() {
  const t = useT();
  const [data, setData] = useState({ items: [], position_vocab: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [error, setError] = useState("");
  const [savingEmail, setSavingEmail] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { setData(await fetchStaffPositions()); }
    catch (e) { setError(e?.response?.data?.detail || e?.message || "Load failed"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onSaveRow = async (row, patch) => {
    setSavingEmail(row.email); setError("");
    try {
      await updateStaffPosition({ email: row.email, ...patch });
      // Optimistic local update
      setData((d) => ({ ...d, items: d.items.map((it) => it.email === row.email ? { ...it, ...patch } : it) }));
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Save failed");
    } finally {
      setSavingEmail("");
    }
  };

  const fl = (filter || "").toLowerCase();
  const items = (data.items || []).filter((it) =>
    !fl || (it.name || "").toLowerCase().includes(fl)
       || (it.email || "").toLowerCase().includes(fl)
       || (it.position || "").toLowerCase().includes(fl)
       || (it.department || "").toLowerCase().includes(fl)
  );

  return (
    <div className="space-y-4">
      {error && <ErrorBanner error={error} onClose={() => setError("")} />}
      <div className="flex items-center gap-3">
        <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={t("call_routing.staff_filter_placeholder", "Filter by name / email / position…")} className="h-9 text-sm flex-1 max-w-md" data-testid="staff-filter" />
        <button onClick={load} disabled={loading} className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-slate-300 rounded-sm hover:bg-slate-100 disabled:opacity-50" data-testid="staff-refresh">
          {loading ? <Spinner size={12} className="animate-spin" /> : <ArrowsClockwise size={12} />} {t("action.refresh", "Refresh")}
        </button>
      </div>

      <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th>Department</Th><Th>Position</Th><Th></Th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <StaffRow key={it.email} row={it} vocab={data.position_vocab || []}
                saving={savingEmail === it.email} onSave={onSaveRow} />
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="py-12 text-center text-sm text-slate-500" data-testid="staff-empty">{t("call_routing.no_staff_match", "No staff match.")}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="text-[11px] text-slate-500 inline-flex items-center gap-1">
        <ShieldCheck size={12} weight="fill" />
        Position values come from the Employee Positions table. Phone numbers are normalised to E.164 on save.
      </div>
    </div>
  );
}

function StaffRow({ row, vocab, saving, onSave }) {
  const t = useT();
  const [edit, setEdit] = useState(null); // null = not editing, object = patch in flight
  const buf = edit || { position: row.position || "", department: row.department || "", phone: row.phone || "" };
  const dirty = !!edit && (
    buf.position !== (row.position || "") ||
    buf.department !== (row.department || "") ||
    buf.phone !== (row.phone || "")
  );
  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50" data-testid={`staff-row-${row.email}`}>
      <td className="px-3 py-2 font-bold text-slate-800">{row.name || "—"}</td>
      <td className="px-3 py-2 text-slate-600 font-mono text-[10px]">{row.email}</td>
      <td className="px-3 py-2">
        {edit ? (
          <Input value={buf.phone || ""} onChange={(e) => setEdit({ ...buf, phone: e.target.value })}
            className="text-xs font-mono h-7" data-testid={`staff-${row.email}-phone`} />
        ) : <span className="font-mono text-[11px] text-slate-700">{row.phone || "—"}</span>}
      </td>
      <td className="px-3 py-2">
        {edit ? (
          <Input value={buf.department || ""} onChange={(e) => setEdit({ ...buf, department: e.target.value })}
            className="text-xs h-7" data-testid={`staff-${row.email}-dept`} />
        ) : <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-sm"><Buildings size={9} weight="fill" />{row.department || "—"}</span>}
      </td>
      <td className="px-3 py-2">
        {edit ? (
          <select value={buf.position || ""} onChange={(e) => setEdit({ ...buf, position: e.target.value })}
            className="text-xs h-7 border border-slate-300 rounded-sm bg-white px-1" data-testid={`staff-${row.email}-position`}>
            <option value="">— None —</option>
            {Array.from(new Set(vocab.map((v) => (v.position || "").trim()).filter(Boolean))).map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
            {row.position && !vocab.some((v) => v.position === row.position) && (
              <option value={row.position}>{row.position} (custom)</option>
            )}
          </select>
        ) : <span className="text-slate-700 font-bold">{row.position || "—"}</span>}
      </td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        {edit ? (
          <>
            <button onClick={() => setEdit(null)} disabled={saving} className="text-[10px] uppercase tracking-wider text-slate-500 hover:text-slate-800 mr-2" data-testid={`staff-${row.email}-cancel`}>{t("action.cancel", "Cancel")}</button>
            <button onClick={async () => { await onSave(row, buf); setEdit(null); }} disabled={!dirty || saving} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900 disabled:opacity-40" data-testid={`staff-${row.email}-save`}>
              {saving ? <Spinner size={11} className="animate-spin" /> : <FloppyDisk size={11} weight="fill" />} {t("action.save", "Save")}
            </button>
          </>
        ) : (
          <button onClick={() => setEdit({ position: row.position || "", department: row.department || "", phone: row.phone || "" })} className="text-[10px] uppercase tracking-wider text-blue-700 hover:text-blue-900 font-bold" data-testid={`staff-${row.email}-edit`}>{t("action.edit", "Edit")}</button>
        )}
      </td>
    </tr>
  );
}

// ─── Webhook sync tab ────────────────────────────────────────────────
function WebhookTab() {
  const t = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { setData(await previewVoiceWebhooks()); }
    catch (e) { setError(e?.response?.data?.detail || e?.message || "Load failed"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onSync = async () => {
    setSyncing(true); setError(""); setResult(null);
    try {
      const r = await syncVoiceWebhooks(null);
      setResult(r);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-4">
      {error && <ErrorBanner error={error} onClose={() => setError("")} />}
      <Card
        title={t("call_routing.webhook_drift_title", "Twilio voice webhook drift")}
        sub={t("call_routing.webhook_drift_sub", "Each Twilio phone number must point its voice URL at our /api/twilio/voice/inbound. After a fork or env change, click Sync to repoint.")}
      >
        {loading && !data ? <div className="p-6 text-center"><Spinner size={20} className="animate-spin text-slate-400" /></div> : (
          <>
            <div className="text-[11px] font-mono bg-slate-50 border border-slate-200 rounded-sm p-2 mb-3">
              <div><span className="text-slate-500">{t("call_routing.expected_voice_url", "Expected voice_url:")} </span>{data?.expected?.voice_url}</div>
              <div><span className="text-slate-500">{t("call_routing.status_callback", "Status callback:")} </span>{data?.expected?.status_callback}</div>
            </div>
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-y border-slate-200">
                <tr><Th>Phone</Th><Th>Friendly</Th><Th>{t("call_routing.current_voice_url", "Current voice_url")}</Th><Th>State</Th></tr>
              </thead>
              <tbody>
                {(data?.items || []).map((it) => (
                  <tr key={it.sid} className="border-b border-slate-100" data-testid={`webhook-row-${it.sid}`}>
                    <td className="px-3 py-2 font-mono text-[11px]">{it.phone_number}</td>
                    <td className="px-3 py-2 text-slate-600">{it.friendly_name || "—"}</td>
                    <td className="px-3 py-2 font-mono text-[10px] text-slate-500 truncate max-w-md" title={it.voice_url}>{it.voice_url || "—"}</td>
                    <td className="px-3 py-2">
                      {it.in_sync
                        ? <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700"><CheckCircle size={10} weight="fill" /> in sync</span>
                        : <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-700"><Warning size={10} weight="fill" /> drift</span>}
                    </td>
                  </tr>
                ))}
                {(!data?.items?.length) && <tr><td colSpan={4} className="py-8 text-center text-sm text-slate-500">{t("call_routing.no_twilio_numbers", "No Twilio numbers found.")}</td></tr>}
              </tbody>
            </table>
          </>
        )}
        <div className="mt-3 flex items-center gap-3">
          <button onClick={onSync} disabled={syncing} className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-4 py-2 bg-blue-700 text-white rounded-sm hover:bg-blue-800 disabled:bg-slate-300" data-testid="sync-webhooks-btn">
            {syncing ? <><Spinner size={12} className="animate-spin" /> Syncing…</> : <><ArrowRight size={12} weight="bold" /> Sync now</>}
          </button>
          {result && (
            <span className="text-[11px] text-slate-600">
              Updated <strong className="text-emerald-700">{result.updated.length}</strong> · Skipped {result.skipped.length} · Failed <strong className={result.failed.length ? "text-rose-700" : ""}>{result.failed.length}</strong>
            </span>
          )}
        </div>
      </Card>
    </div>
  );
}

// ─── Briefings tab — pre-call warranty SMS audit log ────────────────
function BriefingsTab() {
  const t = useT();
  const [data, setData] = useState({ items: [], summary: {}, since_hours: 168 });
  const [loading, setLoading] = useState(true);
  const [hours, setHours] = useState(168);
  const [error, setError] = useState("");

  const load = useCallback(async (h = hours) => {
    setLoading(true); setError("");
    try { setData(await fetchWarrantyBriefings({ limit: 100, since_hours: h })); }
    catch (e) { setError(e?.response?.data?.detail || e?.message || "Load failed"); }
    finally { setLoading(false); }
  }, [hours]);
  useEffect(() => { load(hours); }, [load, hours]);

  const sent = data.summary?.sent || 0;
  const failed = data.summary?.failed || 0;
  const skipped = data.summary?.skipped || 0;
  const total = sent + failed + skipped;
  const successRate = total > 0 ? Math.round((sent / total) * 100) : 0;

  return (
    <div className="space-y-4">
      {error && <ErrorBanner error={error} onClose={() => setError("")} />}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total" value={total} subtitle={`last ${hours}h`} />
        <Stat label="Sent" value={sent} subtitle={`${successRate}% success`} tone="emerald" />
        <Stat label="Failed" value={failed} subtitle="needs attention" tone={failed > 0 ? "rose" : "slate"} />
        <Stat label="Skipped" value={skipped} subtitle="no claim / no phone" tone="slate" />
      </div>

      <div className="bg-white rounded-md border border-slate-200">
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center gap-3">
          <div>
            <h2 className="text-sm font-bold tracking-tight font-['Chivo'] flex items-center gap-2">
              <Wrench size={14} weight="fill" className="text-blue-700" />
              Briefing delivery log
            </h2>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Each row is a pre-call SMS attempt fired the moment Twilio rang the rep. Failures here = blind reps.
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <select value={hours} onChange={(e) => setHours(parseInt(e.target.value, 10))}
              className="text-xs border border-slate-300 rounded-sm bg-white px-2 py-1"
              data-testid="briefings-window">
              <option value={24}>{t("call_routing.last_24h", "Last 24h")}</option>
              <option value={72}>{t("hot_leads.last_3_days", "Last 3 days")}</option>
              <option value={168}>{t("hot_leads.last_7_days", "Last 7 days")}</option>
              <option value={720}>{t("hot_leads.last_30_days", "Last 30 days")}</option>
            </select>
            <button onClick={() => load(hours)} disabled={loading}
              className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-slate-300 rounded-sm hover:bg-slate-100 disabled:opacity-50"
              data-testid="briefings-refresh">
              {loading ? <Spinner size={12} className="animate-spin" /> : <ArrowsClockwise size={12} />} {t("action.refresh", "Refresh")}
            </button>
          </div>
        </div>

        <div className="overflow-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <Th>When</Th><Th>Outcome</Th><Th>Customer</Th>
                <Th>Claim</Th><Th>Rep</Th><Th>{t("call_routing.body_error", "Body / Error")}</Th>
              </tr>
            </thead>
            <tbody>
              {(data.items || []).map((it) => (
                <BriefingRow key={it.id} row={it} />
              ))}
              {!loading && (data.items || []).length === 0 && (
                <tr><td colSpan={6} className="py-12 text-center text-sm text-slate-500" data-testid="briefings-empty">
                  {t("call_routing.no_briefings_prefix", "No briefings in the last")} {hours}{t("call_routing.no_briefings_suffix", "h. Try a wider window above, or trigger an inbound call from a known customer with an open claim.")}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function BriefingRow({ row }) {
  const t = useT();
  const outcome = (row.outcome || "skipped").toLowerCase();
  const tone = outcome === "sent" ? "emerald" : outcome === "failed" ? "rose" : "slate";
  const when = (() => {
    try {
      const d = new Date(row.created_at);
      return d.toLocaleString(undefined, { hour: "numeric", minute: "2-digit", month: "short", day: "numeric" });
    } catch { return row.created_at || ""; }
  })();
  return (
    <tr className="border-b border-slate-100 align-top hover:bg-slate-50" data-testid={`briefing-row-${row.id}`}>
      <td className="px-3 py-2 whitespace-nowrap text-slate-700 font-mono text-[10px]">{when}</td>
      <td className="px-3 py-2">
        <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${
          tone === "emerald" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
          tone === "rose"    ? "bg-rose-50 text-rose-700 border-rose-200" :
                               "bg-slate-100 text-slate-600 border-slate-200"
        }`} data-testid={`briefing-${row.id}-outcome`}>
          {outcome === "sent" ? <CheckCircle size={10} weight="fill" /> : outcome === "failed" ? <Warning size={10} weight="fill" /> : <X size={10} weight="bold" />}
          {outcome}
        </span>
      </td>
      <td className="px-3 py-2">
        <div className="font-bold text-slate-800 truncate max-w-[160px]" title={row.company_name}>{row.company_name || "—"}</div>
        <div className="font-mono text-[10px] text-slate-500">{row.caller_phone || "—"}</div>
      </td>
      <td className="px-3 py-2">
        {row.claim_ticket ? (
          <a href={row.claim_id ? `/warranty/claims/${row.claim_id}` : "#"}
             className="inline-flex items-center gap-1 text-[11px] font-mono text-blue-700 hover:underline"
             target="_blank" rel="noreferrer">
            {row.claim_ticket}
            {row.claim_id && <ArrowSquareOut size={10} weight="bold" />}
          </a>
        ) : <span className="text-slate-400">—</span>}
        <div className="text-[10px] text-slate-500 capitalize">{(row.claim_status || "").replace(/_/g, " ")}</div>
      </td>
      <td className="px-3 py-2">
        <div className="text-slate-700 font-mono text-[10px] truncate max-w-[140px]" title={row.rep_email}>{row.rep_email || "—"}</div>
        <div className="font-mono text-[10px] text-slate-500">{row.rep_phone_e164 || "—"}</div>
      </td>
      <td className="px-3 py-2">
        {outcome === "failed" || outcome === "skipped" ? (
          <div className="text-[11px] text-rose-700">{row.error || "—"}</div>
        ) : (
          <details className="text-[10px]">
            <summary className="cursor-pointer text-slate-500 hover:text-slate-700">{row.body_len || 0} {t("call_routing.chars_sent", "chars sent")}</summary>
            <pre className="whitespace-pre-wrap break-all text-[10px] text-slate-700 bg-slate-50 border border-slate-200 rounded-sm p-2 mt-1 max-w-md">{row.body}</pre>
          </details>
        )}
      </td>
    </tr>
  );
}

function Stat({ label, value, subtitle, tone = "blue" }) {
  const palette = {
    blue:    { bg: "bg-blue-50",    border: "border-blue-200",    text: "text-blue-800" },
    emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800" },
    rose:    { bg: "bg-rose-50",    border: "border-rose-200",    text: "text-rose-800" },
    slate:   { bg: "bg-slate-50",   border: "border-slate-200",   text: "text-slate-800" },
  }[tone] || { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-800" };
  return (
    <div className={`${palette.bg} ${palette.border} ${palette.text} border rounded-md p-3`} data-testid={`stat-${label.toLowerCase()}`}>
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-70">{label}</div>
      <div className="text-2xl font-black font-['Chivo'] mt-0.5">{value}</div>
      <div className="text-[10px] opacity-70 mt-0.5">{subtitle}</div>
    </div>
  );
}


// ─── Errors tab — unhandled 500s log (TTL 7d) ────────────────────────
function ErrorsTab({ user }) {
  const t = useT();
  const [data, setData] = useState({ items: [], summary: { by_exc_type: [], by_path: [] } });
  const [loading, setLoading] = useState(true);
  const [hours, setHours] = useState(168);
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState("");
  const [openRow, setOpenRow] = useState(null);

  const reqEmail = user?.email;

  const load = useCallback(async (h = hours) => {
    setLoading(true); setError("");
    try { setData(await fetchErrorLog(reqEmail, { limit: 100, since_hours: h })); }
    catch (e) { setError(e?.response?.data?.detail || e?.message || "Load failed"); }
    finally { setLoading(false); }
  }, [hours, reqEmail]);
  useEffect(() => { load(hours); }, [load, hours]);

  const openIncident = async (id) => {
    setOpenId(id); setOpenRow(null);
    try { setOpenRow(await fetchErrorRow(id, reqEmail)); }
    catch (e) { setError(e?.response?.data?.detail || e?.message || "Could not load incident"); }
  };

  const total = (data.items || []).length;
  const topType = data.summary?.by_exc_type?.[0];
  const topPath = data.summary?.by_path?.[0];

  return (
    <div className="space-y-4">
      {error && <ErrorBanner error={error} onClose={() => setError("")} />}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Errors" value={total} subtitle={`last ${hours}h`} tone={total > 0 ? "rose" : "emerald"} />
        <Stat label={t("call_routing.top_type", "Top type")} value={topType?.key || "—"} subtitle={topType?.count ? `${topType.count}× ` : ""} tone="slate" />
        <Stat label={t("call_routing.top_path", "Top path")} value={(topPath?.key || "—").slice(0, 24)} subtitle={topPath?.count ? `${topPath.count}× ` : ""} tone="slate" />
        <Stat label="TTL" value="7d" subtitle="auto-purge" tone="blue" />
      </div>

      <div className="bg-white rounded-md border border-slate-200">
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center gap-3">
          <div>
            <h2 className="text-sm font-bold tracking-tight font-['Chivo'] flex items-center gap-2">
              <Bug size={14} weight="fill" className="text-rose-700" />
              Server error log
            </h2>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {t("call_routing.errors_log_hint", "Every backend 500 lands here with a stack trace. Rows older than 7 days auto-purge.")}
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <select value={hours} onChange={(e) => setHours(parseInt(e.target.value, 10))}
              className="text-xs border border-slate-300 rounded-sm bg-white px-2 py-1"
              data-testid="errors-window">
              <option value={1}>{t("call_routing.last_1h", "Last 1h")}</option>
              <option value={24}>{t("call_routing.last_24h", "Last 24h")}</option>
              <option value={72}>{t("hot_leads.last_3_days", "Last 3 days")}</option>
              <option value={168}>{t("hot_leads.last_7_days", "Last 7 days")}</option>
            </select>
            <button onClick={() => load(hours)} disabled={loading}
              className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-slate-300 rounded-sm hover:bg-slate-100 disabled:opacity-50"
              data-testid="errors-refresh">
              {loading ? <Spinner size={12} className="animate-spin" /> : <ArrowsClockwise size={12} />} {t("action.refresh", "Refresh")}
            </button>
          </div>
        </div>

        <div className="overflow-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr><Th>When</Th><Th>Incident</Th><Th>Type</Th><Th>{t("call_routing.method_path", "Method · Path")}</Th><Th>Actor</Th><Th>Message</Th></tr>
            </thead>
            <tbody>
              {(data.items || []).map((it) => (
                <tr key={it.id} className="border-b border-slate-100 hover:bg-rose-50/40 cursor-pointer"
                    onClick={() => openIncident(it.id)} data-testid={`error-row-${it.id}`}>
                  <td className="px-3 py-2 whitespace-nowrap text-slate-700 font-mono text-[10px]">
                    {(() => {
                      try { return new Date(it.created_at).toLocaleString(undefined, { hour: "numeric", minute: "2-digit", month: "short", day: "numeric" }); }
                      catch { return it.created_at; }
                    })()}
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px] text-rose-700 font-bold">{it.id}</td>
                  <td className="px-3 py-2"><span className="text-[10px] font-bold uppercase tracking-wider bg-rose-50 text-rose-700 border border-rose-200 px-1.5 py-0.5 rounded-sm">{it.exc_type}</span></td>
                  <td className="px-3 py-2 font-mono text-[10px] text-slate-600 truncate max-w-[280px]" title={it.path}>{it.method} {it.path}</td>
                  <td className="px-3 py-2 text-slate-700 font-mono text-[10px] truncate max-w-[160px]">{it.actor_email || "—"}</td>
                  <td className="px-3 py-2 text-slate-700 truncate max-w-[280px]" title={it.exc_message}>{(it.exc_message || "").slice(0, 80)}</td>
                </tr>
              ))}
              {!loading && (data.items || []).length === 0 && (
                <tr><td colSpan={6} className="py-12 text-center text-sm text-emerald-700" data-testid="errors-empty">
                  <CheckCircle size={20} weight="fill" className="mx-auto mb-2 text-emerald-500" />
                  No 500 errors in the last {hours}h. Nice work.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {openId && (
        <ErrorIncidentDialog
          row={openRow}
          onClose={() => { setOpenId(""); setOpenRow(null); }}
        />
      )}
    </div>
  );
}

function ErrorIncidentDialog({ row, onClose }) {
  const t = useT();
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="error-incident-dialog">
      <div className="bg-white rounded-md shadow-xl max-w-3xl w-full max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-slate-200 sticky top-0 bg-white flex items-center gap-3">
          <Bug size={16} weight="fill" className="text-rose-700" />
          <h3 className="text-sm font-bold tracking-tight font-['Chivo']">
            Incident <span className="font-mono text-rose-700">{row?.id || "…"}</span>
          </h3>
          <button onClick={onClose} className="ml-auto w-7 h-7 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500" data-testid="error-incident-close">
            <X size={14} weight="bold" />
          </button>
        </div>
        {!row ? (
          <div className="p-8 text-center"><Spinner size={20} className="animate-spin text-slate-400 mx-auto" /></div>
        ) : (
          <div className="p-4 space-y-3 text-xs">
            <div className="grid grid-cols-2 gap-3">
              <KV k="Type" v={row.exc_type} />
              <KV k="When" v={row.created_at} />
              <KV k={t("call_routing.method_path", "Method · Path")} v={`${row.method} ${row.path}`} />
              <KV k="Actor" v={row.actor_email || "—"} />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">Message</div>
              <div className="text-slate-800">{row.exc_message}</div>
            </div>
            {row.query_string && (
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">{t("call_routing.query_string", "Query string")}</div>
                <pre className="font-mono text-[10px] bg-slate-50 border border-slate-200 rounded-sm p-2 overflow-auto whitespace-pre-wrap break-all">{row.query_string}</pre>
              </div>
            )}
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">Traceback</div>
              <pre className="font-mono text-[10px] bg-slate-900 text-slate-100 rounded-sm p-3 overflow-auto whitespace-pre">{row.traceback || "—"}</pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{k}</div>
      <div className="font-mono text-[11px] text-slate-800 break-all">{v || "—"}</div>
    </div>
  );
}


// ─── Shared bits ─────────────────────────────────────────────────────
function Card({ title, sub, children }) {
  return (
    <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
        <h2 className="text-sm font-bold tracking-tight font-['Chivo'] flex items-center gap-2"><Phone size={14} weight="fill" className="text-blue-700" />{title}</h2>
        {sub && <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p>}
      </div>
      <div className="p-4 space-y-3">{children}</div>
    </div>
  );
}

function FieldRow({ label, children, small }) {
  return (
    <label className="block">
      <div className={`text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-1 ${small ? "" : ""}`}>{label}</div>
      {children}
    </label>
  );
}

function ErrorBanner({ error, onClose }) {
  return (
    <div className="bg-rose-50 border border-rose-200 text-rose-800 text-sm rounded-sm px-3 py-2 flex items-start gap-2" data-testid="call-routing-error">
      <Warning size={14} weight="fill" className="text-rose-600 shrink-0 mt-0.5" />
      <span className="flex-1">{typeof error === "string" ? error : JSON.stringify(error)}</span>
      <button onClick={onClose} className="text-rose-500 hover:text-rose-700"><X size={12} weight="bold" /></button>
    </div>
  );
}

function Th({ children }) {
  return <th className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 text-left">{children}</th>;
}
